import { useCallback, useEffect, useMemo, useState } from "react";
import { sitesApi } from "@/network/network.jsx";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import { getApiErrorMessage } from "@/integrations";
import { Site } from "@/bryntum-lib/models/Site";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import { extractAsyncTaskId } from "@/modern/components/async-tasks/utils";
import { useSitePublishBridge } from "../context/MarketingPagesContext";
import {
  ensureSiteInStore,
  findSiteForBoard,
  getSitePublishButtonState,
  getSitePublishLiveButtonState,
  isSandboxBoardRecord,
  isSiteLive,
  TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
  siteNeedsPersist,
} from "../utils";

function extractAsyncTaskType(data: unknown): number | null {
  if (data == null || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const nested =
    record.async_task != null && typeof record.async_task === "object"
      ? (record.async_task as Record<string, unknown>)
      : null;
  const type = nested?.type ?? record.type;
  return typeof type === "number" ? type : null;
}

interface UseSiteOptions {
  initialSite?: Site | null;
  /** When true (Site page), persist phantom site on mount. */
  syncOnMount?: boolean;
}

export function useSite(options: UseSiteOptions = {}) {
  const { initialSite, syncOnMount = false } = options;
  const { project, selectedBoard, user } = useSwivuBryntumContext();
  const boardRecord = selectedBoard?.record as
    | { slug?: string; isSandbox?: boolean }
    | undefined;
  const boardSlug = boardRecord?.slug;
  const boardId = selectedBoard?.id as number | undefined;
  const isTestDrive = isSandboxBoardRecord(boardRecord);
  // Test Drive: Site actions disabled even if an orphan unpublished row exists.
  const isReadOnly = !user?.can?.(GRANTS.grant_four) || isTestDrive;
  const { pinTask } = useAsyncTaskContext();
  const {
    setManualPublish,
    setPublishLiveTaskId,
    publishSuccessNonce,
    isPublishing,
    isRepublishing,
    isDeploying,
  } = useSitePublishBridge();

  const { version: pagesVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingPageStore });
  const { version: sitesVersion } = useSwivuStore({ storeId: STORE_KEYS.sitesStore });

  const [activeSite, setActiveSite] = useState<Site | null>(initialSite ?? null);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const [ensureError, setEnsureError] = useState<string | null>(null);

  const siteRecord = useMemo<Site | null>(() => {
    if (!project || !boardId) return null;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    return findSiteForBoard(store, boardId);
  }, [project, sitesVersion, boardId]);

  const includedPages = useMemo(() => {
    if (!project) return [] as MarketingPage[];
    const store = project.getCrudStore(STORE_KEYS.marketingPageStore);
    const rows = (store?.query(() => true) as unknown as MarketingPage[]) ?? [];
    return rows.filter((p) => p.includeInSite);
  }, [project, pagesVersion]);

  const getOrCreateLocalSite = useCallback((): Site | null => {
    if (!project || !boardId) return null;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    if (!store) return null;

    const existing =
      findSiteForBoard(store, boardId) ??
      (initialSite?.board === boardId ? initialSite : null);
    if (existing) return existing;

    // Never create Site rows on Test Drive boards (API blocks create/sync).
    if (isTestDrive) return null;

    const slug = boardSlug ?? "site";
    const added = store.add({
      board: boardId,
      slug,
      theme: { preset: "light" },
    }) as unknown as Site[];
    return added[0] ?? null;
  }, [project, boardId, boardSlug, initialSite, isTestDrive]);

  const ensureSiteRecord = useCallback((): Site | null => {
    if (isTestDrive) return null;
    return getOrCreateLocalSite();
  }, [getOrCreateLocalSite, isTestDrive]);

  const syncSiteIfNeeded = useCallback(async (): Promise<Site | null> => {
    if (isTestDrive) {
      // Display-only: bind an existing persisted site; never create or sync.
      if (!project || !boardId) {
        setActiveSite(null);
        setEnsureError(null);
        return null;
      }
      const store = project.getCrudStore(STORE_KEYS.sitesStore);
      const existing = findSiteForBoard(store, boardId);
      if (existing && !siteNeedsPersist(existing)) {
        setActiveSite(existing);
        setEnsureError(null);
        return existing;
      }
      setActiveSite(null);
      setEnsureError(null);
      return null;
    }

    if (!project || !boardId) {
      setEnsureError("Board is not ready yet. Try again in a moment.");
      return null;
    }

    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    if (!store) {
      setEnsureError("Site store is unavailable.");
      return null;
    }

    const site = getOrCreateLocalSite();
    if (!site) {
      setEnsureError("Could not prepare site for this board.");
      return null;
    }

    if (!siteNeedsPersist(site)) {
      setActiveSite(site);
      setEnsureError(null);
      return site;
    }

    if (isReadOnly) {
      setActiveSite(site);
      setEnsureError("No site exists for this board yet.");
      return null;
    }

    setIsEnsuring(true);
    setEnsureError(null);
    try {
      ensureSiteInStore(store, site);
      await project.sync();

      const synced = findSiteForBoard(store, boardId);
      if (!synced || siteNeedsPersist(synced)) {
        throw new Error("Site sync did not persist");
      }

      setActiveSite(synced);
      return synced;
    } catch {
      setEnsureError("Could not create site for this board. Try again.");
      setActiveSite(site);
      return null;
    } finally {
      setIsEnsuring(false);
    }
  }, [project, boardId, getOrCreateLocalSite, isReadOnly, isTestDrive]);

  useEffect(() => {
    if (!syncOnMount) return;
    void syncSiteIfNeeded();
  }, [syncOnMount, syncSiteIfNeeded]);

  useEffect(() => {
    if (!project || !boardId) return;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    const synced = findSiteForBoard(store, boardId);
    if (!synced) return;
    if (!activeSite || siteNeedsPersist(activeSite) || synced.id === activeSite.id) {
      setActiveSite(synced);
    }
  }, [project, boardId, sitesVersion, activeSite]);

  const startPublish = useCallback(async () => {
    if (isTestDrive) {
      showToast({ msg: TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG, type: "error", duration: 4000 });
      return false;
    }
    if (isPublishing) {
      return false;
    }
    if (!boardSlug) {
      showToast({ msg: "No board selected.", type: "error", duration: 3000 });
      return false;
    }
    setManualPublish(true);
    try {
      const response = await sitesApi(boardSlug).publish();
      const taskId = extractAsyncTaskId(response.data);
      const ok = response?.status === 202 || taskId != null;
      if (!ok) {
        showToast({ msg: "Could not start site publish.", type: "error", duration: 3000 });
        setManualPublish(false);
        return false;
      }
      if (taskId != null && boardId != null) {
        pinTask(taskId, {
          type: ASYNC_TASK_TYPES.SITE_PUBLISH,
          meta_data: { board_id: boardId },
        });
      }
      return true;
    } catch (error) {
      showToast({
        msg: getApiErrorMessage(error, "Failed to start site publish."),
        type: "error",
        duration: 4000,
      });
      setManualPublish(false);
      return false;
    }
  }, [boardSlug, boardId, isPublishing, isTestDrive, setManualPublish, pinTask]);

  const startPublishWithDomain = useCallback(async () => {
    if (isTestDrive) {
      showToast({ msg: TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG, type: "error", duration: 4000 });
      return false;
    }
    if (isDeploying) {
      return false;
    }
    if (!boardSlug) {
      showToast({ msg: "No board selected.", type: "error", duration: 3000 });
      return false;
    }
    try {
      const response = await sitesApi(boardSlug).publishWithDomain();
      const taskId = extractAsyncTaskId(response.data);
      const ok = response?.status === 202 || taskId != null;
      if (!ok) {
        showToast({ msg: "Could not start publish live.", type: "error", duration: 3000 });
        return false;
      }
      if (taskId != null && boardId != null) {
        const taskType =
          extractAsyncTaskType(response.data) ?? ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY;
        pinTask(taskId, {
          type: taskType,
          meta_data: { board_id: boardId },
        });
        setPublishLiveTaskId(String(taskId));
      }
      return true;
    } catch (error) {
      showToast({
        msg: getApiErrorMessage(error, "Failed to start publish live."),
        type: "error",
        duration: 4000,
      });
      return false;
    }
  }, [boardSlug, boardId, isDeploying, isTestDrive, pinTask, setPublishLiveTaskId]);

  const siteReady = Boolean(
    activeSite && !siteNeedsPersist(activeSite) && !isEnsuring && !ensureError,
  );

  const publishSite = siteRecord ?? activeSite;
  const publishTaskPending = isPublishing || isRepublishing || isDeploying;

  const publishButtonState = useMemo(
    () =>
      getSitePublishButtonState({
        site: publishSite,
        publishTaskPending,
        siteReady,
        isReadOnly,
      }),
    [publishSite, publishTaskPending, siteReady, isReadOnly, sitesVersion],
  );

  const publishLiveButtonState = useMemo(
    () =>
      getSitePublishLiveButtonState({
        site: publishSite,
        publishTaskPending,
        siteReady,
        isReadOnly,
        isLive: isSiteLive(publishSite),
      }),
    [publishSite, publishTaskPending, siteReady, isReadOnly, sitesVersion],
  );

  const testDriveTooltip = isTestDrive ? TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG : null;

  return {
    activeSite,
    setActiveSite,
    siteRecord,
    ensureSiteRecord,
    isEnsuring,
    ensureError,
    syncSiteIfNeeded,
    isPublishing,
    isRepublishing,
    isDeploying,
    publishSuccessNonce,
    publishTaskPending,
    startPublish,
    startPublishWithDomain,
    includedPages,
    publishEnabled: publishButtonState.enabled,
    publishTooltip: testDriveTooltip ?? publishButtonState.tooltip,
    publishLiveEnabled: publishLiveButtonState.enabled,
    publishLiveTooltip: testDriveTooltip ?? publishLiveButtonState.tooltip,
    hasUnpublishedChanges: publishButtonState.hasUnpublishedChanges,
    siteReady,
    isReadOnly,
    isTestDrive,
    boardSlug,
    boardId,
    project,
  };
}
